import {
  Component,
  ComponentRef,
  computed,
  effect,
  input,
  OnDestroy,
  output,
  reflectComponentType,
  Type,
  viewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { ComponentHostDirective } from '@core/directives/component-host.directive';
import { DoodProductModel, TreeCategoryWithProduct } from '@core/models/product.model';
import { takeUntil, tap } from 'rxjs/operators';
import { ContentBuilderColorValue } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ProductPushV2AtomHoverColors } from '@shared/interfaces/product-hover-colors.interface';
import { InterfaceToInputs } from '@shared/utils/types/interface-to-inputs';
import { ProductCategoryColorOverrides } from '@shared/interfaces/product-category-color-overrides.interface';

@Component({
  selector: 'app-category-factory',
  templateUrl: './category-factory.component.html',
  imports: [ComponentHostDirective],
  host: {
    style: 'display: block',
    '[id]': 'sectionId()',
  },
})
export class CategoryFactoryComponent
  implements OnDestroy, InterfaceToInputs<ProductCategoryColorOverrides, { required: true }>
{
  public readonly componentClass = input<Type<unknown>>();
  public readonly isHorizontalHighlight = input(false);
  public readonly isOrderingAllowed = input<boolean>(true);
  public readonly tagColors = input.required<ContentBuilderFieldColors>();
  public readonly treeCategoryWithProduct = input.required<TreeCategoryWithProduct>();
  public readonly navigateTo = output<DoodProductModel>();
  public readonly categoryNameColor = input<ContentBuilderColorValue>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  public readonly categoryDescriptionColor = input<ContentBuilderColorValue>({
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  });
  public readonly highlightCategoryNameColor = input<ContentBuilderColorValue>({
    type: ColorFieldTypesValues.Default,
    value: '',
  });
  public readonly highlightCategoryBackgroundColor = input<ContentBuilderColorValue>({
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  });
  public readonly productNameColor = input<ContentBuilderColorValue>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  public readonly productDescriptionColor = input<ContentBuilderColorValue>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  public readonly productPriceColor = input<ContentBuilderColorValue>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  public readonly productImagePlaceholderColor = input<ContentBuilderColorValue>({
    value: 'neutral-50',
    type: ColorFieldTypesValues.Palette,
  });
  public readonly productBorderColor = input<ContentBuilderColorValue>({
    value: 'neutral-100',
    type: ColorFieldTypesValues.Palette,
  });
  public readonly productBackgroundColor = input<ContentBuilderColorValue>({
    value: 'transparent',
    type: ColorFieldTypesValues.Hex,
  });
  readonly productHoverColors = input<ProductPushV2AtomHoverColors | undefined>(undefined);

  protected readonly componentHost = viewChild(ComponentHostDirective);
  protected readonly sectionId = computed(() => this.treeCategoryWithProduct().category.id);

  private readonly destroyed$ = new Subject<boolean>();
  private componentRef?: ComponentRef<unknown>;

  constructor() {
    effect(() => {
      const componentHost = this.componentHost();
      const componentClass = this.componentClass();

      if (!componentHost || !componentClass) {
        return;
      }

      if (this.componentRef) {
        this.componentRef.destroy();
        this.componentRef = undefined;
      }

      componentHost.viewContainerRef.clear();
      const viewContainerRef = componentHost.viewContainerRef;

      this.componentRef = viewContainerRef.createComponent<unknown>(componentClass);

      // Always set inputs so category title is shown even when category has no direct products (e.g. parent with only subcategories)
      this.setComponentRefInputs();

      const instance = this.componentRef.instance as {
        navigateTo?: {
          pipe: (...operators: unknown[]) => {
            subscribe: (callback?: (value: DoodProductModel) => void) => void;
          };
        };
      };
      if (instance.navigateTo && typeof instance.navigateTo.pipe === 'function') {
        instance.navigateTo
          .pipe(
            takeUntil(this.destroyed$),
            tap(($event: DoodProductModel) => {
              this.navigateTo.emit($event);
            }),
          )
          .subscribe();
      }
    });

    // Keep dynamic category atom inputs in sync via setInput only.
    // Never Object.assign onto signal-input instances — that replaces InputSignal
    // functions and makes later setInput crash (transformFn of undefined).
    effect(() => {
      this.treeCategoryWithProduct();
      this.isOrderingAllowed();
      this.categoryNameColor();
      this.categoryDescriptionColor();
      this.highlightCategoryNameColor();
      this.highlightCategoryBackgroundColor();
      this.productNameColor();
      this.productDescriptionColor();
      this.productPriceColor();
      this.productImagePlaceholderColor();
      this.productBorderColor();
      this.productBackgroundColor();
      this.tagColors();
      this.productHoverColors();

      if (this.componentRef) {
        this.setComponentRefInputs();
      }
    });
  }

  public onNavigateTo(product: DoodProductModel): void {
    this.navigateTo.emit(product);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private setComponentRefInputs(): void {
    const componentType = this.componentClass();
    if (!componentType || !this.componentRef) {
      return;
    }

    const inputsByPropName = new Map(
      (reflectComponentType(componentType)?.inputs ?? []).map(inputMeta => [
        inputMeta.propName,
        inputMeta.templateName,
      ]),
    );

    const treeCategory = this.treeCategoryWithProduct();
    const inputMappings: Record<string, unknown> = {
      category: treeCategory.category,
      products: treeCategory.products,
      isOrderingAllowed: this.isOrderingAllowed(),
      level: treeCategory.level,
      categoryNameColor: this.categoryNameColor(),
      categoryDescriptionColor: this.categoryDescriptionColor(),
      highlightCategoryNameColor: this.highlightCategoryNameColor(),
      highlightCategoryBackgroundColor: this.highlightCategoryBackgroundColor(),
      productNameColor: this.productNameColor(),
      productDescriptionColor: this.productDescriptionColor(),
      productPriceColor: this.productPriceColor(),
      productImagePlaceholderColor: this.productImagePlaceholderColor(),
      productBorderColor: this.productBorderColor(),
      productBackgroundColor: this.productBackgroundColor(),
      tagColors: this.tagColors(),
      productHoverColors: this.productHoverColors(),
    };

    Object.entries(inputMappings).forEach(([propName, value]) => {
      const templateName = inputsByPropName.get(propName);
      if (templateName === undefined) {
        return;
      }
      try {
        this.componentRef!.setInput(templateName, value);
      } catch (error) {
        console.warn(`[CategoryFactory] Failed to set input "${templateName}"`, error);
      }
    });
  }
}
